import React from 'react';
import './ModernButton.css';

export interface ModernButtonProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  icon?: string;
  iconPosition?: 'left' | 'right';
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  style?: React.CSSProperties;
  fullWidth?: boolean;
  title?: string;
}

export const ModernButton: React.FC<ModernButtonProps> = ({
  variant = 'primary',
  size = 'md',
  type = 'button',
  disabled = false,
  loading = false,
  icon,
  iconPosition = 'left',
  children,
  onClick,
  className = '',
  style,
  fullWidth = false,
  title,
}) => {
  const baseClass = 'modern-btn';
  const variantClass = `modern-btn--${variant}`;
  const sizeClass = `modern-btn--${size}`;
  const disabledClass = disabled || loading ? 'modern-btn--disabled' : '';
  const loadingClass = loading ? 'modern-btn--loading' : '';
  const fullWidthClass = fullWidth ? 'modern-btn--full-width' : '';

  const classes = [
    baseClass,
    variantClass,
    sizeClass,
    disabledClass,
    loadingClass,
    fullWidthClass,
    className
  ].filter(Boolean).join(' ');

  const renderIcon = (position: 'left' | 'right') => {
    if (!icon || iconPosition !== position) return null;
    // Hide the icon when loading to avoid duplication with spinner
    if (loading) return null;
    
    return (
      <i 
        className={`${icon} modern-btn__icon modern-btn__icon--${position}`}
        aria-hidden="true"
      />
    );
  };

  const renderSpinner = () => {
    if (!loading) return null;
    
    return (
      <span className="modern-btn__spinner-icon" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.3" />
          <path d="M8 2a6 6 0 0 1 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </span>
    );
  };

  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={classes}
      style={style}
      title={title}
      aria-disabled={disabled || loading}
    >
      {loading && renderSpinner()}
      {renderIcon('left')}
      {children && <span className="modern-btn__text">{children}</span>}
      {renderIcon('right')}
    </button>
  );
};

export default ModernButton;
